// frontend/src/pages/RelogioEnergia.js
import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import "./RelogioAgua.css";
import BuscarMatricula from "../components/BuscarMatricula";
import { copiarTexto } from "../utils/clipboard";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { FaEye, FaEdit, FaTimes, FaTrash, FaSave, FaPaperclip } from "react-icons/fa";
import muiTheme from "../theme/muiTheme";
import { mascararCpfCnpj } from "../utils/documento";

// =====================
// MÁSCARAS
// =====================

// CPF ou CNPJ (numérico ou alfanumérico, letras A-Z nas 12 primeiras posições).
const maskCpfCnpj = (value) => mascararCpfCnpj(value);

// N° Relógio → apenas números
const maskNumero = (value) => value.replace(/\D/g, "").slice(0, 15);

// Valor em dinheiro → 1.234,56
const maskMoney = (value) => {
  let v = String(value ?? "").replace(/\D/g, "");
  v = (Number(v) / 100).toFixed(2) + "";
  return v.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

function RelogioEnergia() {
  const [locais, setLocais] = useState([]);
  const [registros, setRegistros] = useState([]);

  const [formData, setFormData] = useState({
    codigo: "",
    apelido: "",
    complemento: "",
    numeroRelogio: "",
    subNumeroRelogio: "",
    categoriaConsumo: "",
    nomeProprietario: "",
    cpfProprietario: "",
    nomeTitular: "",
    cpfTitular: "",
    status: "",
    debitoAberto: "",
    valorDebito: "",
    competencia: "",
    atualizado: "",
    observacoes: "",
    matricula: "",
    matriculaId: "", // importante para BuscarMatricula
  });

  const [filtroCodigo, setFiltroCodigo] = useState("");
  const [nomeProprietarioMatricula, setNomeProprietarioMatricula] = useState("");
  const [nomeLocatario, setNomeLocatario] = useState("");

  // =========================
  // BUSCA DE IMÓVEL PELO APELIDO
  // =========================
  const [imoveisBuscaLista, setImoveisBuscaLista] = useState([]);
  const [modalSelecionarImovel, setModalSelecionarImovel] = useState(false);
  const [filtroImovel, setFiltroImovel] = useState("");
  const [paginaImovel, setPaginaImovel] = useState(0);
  const [linhasPorPaginaImovel, setLinhasPorPaginaImovel] = useState(10);

  // GED
  const [arquivos, setArquivos] = useState([]);
  const [modalGED, setModalGED] = useState(false);
  const [codigoSelecionadoGED, setCodigoSelecionadoGED] = useState(null);
  const [gedPendentes, setGedPendentes] = useState([]);

  // =========================
  // ✅ minimizar/expandir grid (persistido)
  // =========================
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("relogioEnergiaGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("relogioEnergiaGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // =========================
  // NORMALIZAÇÃO
  // =========================
  const normalizarRegistro = (obj) => {
    const lower = Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [String(k).toLowerCase(), v]));

    return {
      ...obj,
      ...lower,

      codigo: lower.codigo ?? obj.codigo ?? "",
      apelido: lower.apelido ?? obj.apelido ?? "",
      complemento: lower.complemento ?? obj.complemento ?? "",
      numeroRelogio: lower.numerorelogio ?? obj.numeroRelogio ?? "",
      subNumeroRelogio: lower.subnumerorelogio ?? obj.subNumeroRelogio ?? "",
      categoriaConsumo: lower.categoriaconsumo ?? obj.categoriaConsumo ?? "",
      nomeProprietario: lower.nomeproprietario ?? obj.nomeProprietario ?? "",
      cpfProprietario: lower.cpfproprietario ?? obj.cpfProprietario ?? "",
      nomeTitular: lower.nometitular ?? obj.nomeTitular ?? "",
      cpfTitular: lower.cpftitular ?? obj.cpfTitular ?? "",
      status: String(lower.status ?? obj.status ?? "")
        .trim()
        .toUpperCase(),
      debitoAberto: String(lower.debitoaberto ?? obj.debitoAberto ?? "")
        .trim()
        .toUpperCase(),
      valorDebito: lower.valordebito ?? obj.valorDebito ?? "",
      competencia: lower.competencia ?? obj.competencia ?? "",
      atualizado: String(lower.atualizado ?? obj.atualizado ?? "")
        .trim()
        .toUpperCase(),
      observacoes: lower.observacoes ?? obj.observacoes ?? "",
      matricula: lower.matricula ?? obj.matricula ?? "",
      matriculaId: lower.matriculaid ?? obj.matriculaId ?? "",
      qtdArquivos: lower.qtdarquivos ?? obj.qtdArquivos ?? 0,
    };
  };

  const definirRegistros = (data) => {
    const lista = Array.isArray(data)
      ? data
      : Array.isArray(data?.recordset)
        ? data.recordset
        : Array.isArray(data?.registros)
          ? data.registros
          : data && typeof data === "object"
            ? [data]
            : [];

    setRegistros(lista.map(normalizarRegistro));
  };

  const [mostrarSituacao, setMostrarSituacao] = useState(false);

  // =========================
  // SISTEMA DE PERMISSÕES
  // =========================
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    try {
      const decoded = jwtDecode(token);

      tipoUsuario = decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    } catch {
      tipoUsuario = "";
    }
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // =========================
  // VISUALIZAR / EDITAR
  // =========================
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [idVisualizando, setIdVisualizando] = useState(null);
  const [editandoCodigo, setEditandoCodigo] = useState(null);

  const formTravado = modoVisualizacao && !editandoCodigo;
  const podeMostrarExcluirNoForm = modoVisualizacao && !editandoCodigo && Boolean(idVisualizando);

  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  const erroCampoMUI = (campo) => {
    if (!mostrarPendencias) return false;
    if (!camposOpcionais.includes(campo)) return false;

    return estaVazio(formData[campo]);
  };

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      display: "flex",
      alignItems: "center",
      backgroundColor: "transparent",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-error:hover fieldset": {
        borderColor: "#ef4444 !important",
      },

      "&.Mui-error.Mui-focused fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-disabled fieldset": {
        borderColor: "var(--ig-b-555555)",
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#ef4444 !important",
    },

    "& .MuiInputLabel-root.Mui-disabled": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiSvgIcon-root": {
      color: "var(--ig-t-ffffff)",
    },

    '& input[type="month"]::-webkit-calendar-picker-indicator': {
      filter: "var(--ig-x-11o99ka)",
      opacity: 1,
      cursor: "pointer",
    },
  };

  const menuPropsDark = {
    MenuProps: {
      PaperProps: {
        sx: {
          backgroundColor: "var(--ig-f-111827)",
          color: "var(--ig-t-ffffff)",

          "& .MuiMenuItem-root": {
            color: "var(--ig-t-ffffff)",
          },

          "& .MuiMenuItem-root:hover": {
            backgroundColor: "var(--ig-f-1f2937)",
          },

          "& .MuiMenuItem-root.Mui-selected": {
            backgroundColor: "var(--ig-f-1f2937) !important",
          },
        },
      },
    },
  };

  const resetForm = () => {
    setFormData({
      codigo: "",
      apelido: "",
      complemento: "",
      numeroRelogio: "",
      subNumeroRelogio: "",
      categoriaConsumo: "",
      nomeProprietario: "",
      cpfProprietario: "",
      nomeTitular: "",
      cpfTitular: "",
      status: "",
      debitoAberto: "",
      valorDebito: "",
      competencia: "",
      atualizado: "",
      observacoes: "",
      matricula: "",
      matriculaId: "",
    });

    setMostrarSituacao(false);
    setModoVisualizacao(false);
    setIdVisualizando(null);
    setEditandoCodigo(null);

    setNomeProprietarioMatricula("");
    setNomeLocatario("");

    setMostrarPendencias(false);

    setGedPendentes([]);
    setArquivos([]);
    setModalGED(false);
    setCodigoSelecionadoGED(null);
    setModalSelecionarImovel(false);
    setFiltroImovel("");
    setPaginaImovel(0);
  };

  const carregarRegistros = async () => {
    const tokenLocal = localStorage.getItem("token");
    if (!tokenLocal) {
      console.warn("Sem token. Faça login novamente.");
      return;
    }

    try {
      const res = await fetch("/api/relogio-energia", {
        headers: { Authorization: `Bearer ${tokenLocal}` },
      });

      const data = await res.json().catch(() => []);

      if (!res.ok) {
        console.error("Erro ao buscar registros:", data);
        return;
      }

      definirRegistros(data);
    } catch (err) {
      console.error("Erro ao buscar registros:", err);
    }
  };

  // =========================
  // PAGINAÇÃO
  // =========================
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(5);

  const registrosFiltrados = Array.isArray(registros)
    ? registros.filter((item) => {
        if (!filtroCodigo.trim()) return true;

        const termos = filtroCodigo
          .toLowerCase()
          .split(/[,;]+/)
          .map((t) => t.trim())
          .filter((t) => t.length > 0);

        const registroTexto = Object.values(item)
          .map((v) => String(v ?? "").toLowerCase())
          .join(" ");

        return termos.every((term) => registroTexto.includes(term));
      })
    : [];

  const totalPaginas = Math.max(1, Math.ceil(registrosFiltrados.length / registrosPorPagina));
  const indexInicial = (paginaAtual - 1) * registrosPorPagina;
  const indexFinal = indexInicial + registrosPorPagina;
  const registrosPaginados = registrosFiltrados.slice(indexInicial, indexFinal);

  // ✅ evita página vazia ao filtrar
  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  const proprietarioIgual = (formData.nomeProprietario ?? "").trim().toLowerCase() === (nomeProprietarioMatricula ?? "").trim().toLowerCase() ? "Sim" : "Não";

  const titularIgual = (formData.nomeTitular ?? "").trim().toLowerCase() === (nomeLocatario ?? "").trim().toLowerCase() ? "Sim" : "Não";

  // =========================
  // FILTRO DO MODAL DE IMÓVEIS
  // =========================
  const imoveisFiltradosBusca = imoveisBuscaLista.filter((imovel) => {
    const termo = filtroImovel
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (!termo) return true;

    const textoImovel = [
      imovel.codigo,
      imovel.codigoMatricula,
      imovel.apelido,
      imovel.endereco,
      imovel.bairro,
      imovel.cidade,
      imovel.estado,
      imovel.uf,
      imovel.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    return textoImovel.includes(termo);
  });

  const imoveisPaginadosBusca = imoveisFiltradosBusca.slice(paginaImovel * linhasPorPaginaImovel, paginaImovel * linhasPorPaginaImovel + linhasPorPaginaImovel);

  const selecionarImovelApelido = (imovel) => {
    setFormData((prev) => ({
      ...prev,
      apelido: String(imovel.apelido || "")
        .trim()
        .toUpperCase(),
    }));

    setModalSelecionarImovel(false);
    setFiltroImovel("");
    setPaginaImovel(0);
  };

  // GED
  const baixarArquivoGED = async (codigo, arquivoId, nome) => {
    try {
      const resp = await fetch(`/api/relogio-energia/${codigo}/ged/${arquivoId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Falha no download");

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = nome || "arquivo";
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Erro ao baixar arquivo.");
    }
  };

  const visualizarArquivoGED = async (codigo, arquivoId, nomeArquivo = "") => {
    if (!codigo || !arquivoId) {
      alert("Arquivo inválido para visualização.");
      return;
    }

    const nome = String(nomeArquivo || arquivoId || "").toLowerCase();

    const extensoesVisualizaveis = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];

    const podeVisualizarArquivo = extensoesVisualizaveis.some((extensao) => nome.endsWith(extensao));

    if (!podeVisualizarArquivo) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\n` +
          `O arquivo "${nomeArquivo}" não possui visualização integrada no ImobGest.\n\n` +
          `Para visualizar esse tipo de arquivo, é necessário baixar e abrir em um aplicativo compatível.\n\n` +
          `Deseja baixar agora?`,
      );

      if (confirmar) {
        baixarArquivoGED(codigo, arquivoId, nomeArquivo);
      }

      return;
    }

    try {
      const resp = await fetch(`/api/relogio-energia/${encodeURIComponent(codigo)}/ged/${encodeURIComponent(arquivoId)}/visualizar`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) {
        const erro = await resp.json().catch(() => ({}));

        throw new Error(erro?.erro || erro?.mensagem || "Erro ao visualizar arquivo GED.");
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error("Erro ao visualizar arquivo GED:", err);

      alert(err?.message || "Erro ao visualizar arquivo GED.");
    }
  };

  const excluirArquivoGED = async (codigo, arquivoId) => {
    if (!window.confirm("Deseja excluir este arquivo?")) return;

    try {
      const resp = await fetch(`/api/relogio-energia/${codigo}/ged/${arquivoId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Erro ao excluir GED");

      setArquivos((old) => old.filter((a) => String(a.id) !== String(arquivoId)));

      await carregarRegistros();

      alert("Arquivo excluído com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir arquivo.");
    }
  };
  const enviarGED = async (codigo, files) => {
    const formDataUpload = new FormData();
    files.forEach((file) => formDataUpload.append("files", file));

    try {
      const resp = await fetch(`/api/relogio-energia/${codigo}/ged`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formDataUpload,
      });

      return resp.ok;
    } catch (err) {
      console.error("Erro ao enviar GED:", err);
      return false;
    }
  };

  const abrirGED = async (codigo) => {
    if (!codigo) return;

    setCodigoSelecionadoGED(codigo);
    setModalGED(true);

    try {
      const resp = await fetch(`/api/relogio-energia/${codigo}/ged`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const dados = await resp.json();
      setArquivos(Array.isArray(dados) ? dados : []);
    } catch (err) {
      console.error("Erro ao carregar arquivos GED:", err);
      setArquivos([]);
    }
  };

  useEffect(() => {
    const carregarArquivosDoForm = async () => {
      const codigoAtual = editandoCodigo || idVisualizando;
      if (!codigoAtual) {
        setArquivos([]);
        return;
      }

      try {
        const resp = await fetch(`/api/relogio-energia/${codigoAtual}/ged`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const dados = await resp.json();
        setArquivos(Array.isArray(dados) ? dados : []);
      } catch {
        setArquivos([]);
      }
    };

    carregarArquivosDoForm();
  }, [editandoCodigo, idVisualizando]);

  // =========================
  // BUSCA DE DADOS
  // =========================
  useEffect(() => {
    const tokenLocal = localStorage.getItem("token");
    if (!tokenLocal) return;

    fetch("/api/complementos", {
      headers: { Authorization: `Bearer ${tokenLocal}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data.complementos) ? data.complementos : Array.isArray(data.recordset) ? data.recordset : [];
        setLocais(lista);
      })
      .catch((err) => console.error("Erro ao buscar complementos:", err));

    fetch("/api/relogio-energia", {
      headers: { Authorization: `Bearer ${tokenLocal}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          console.error("Erro ao buscar registros:", data);
          return;
        }

        definirRegistros(data);
      })
      .catch((err) => {
        console.error("Erro ao buscar registros:", err);
      });

    fetch("/api/imoveis", {
      headers: {
        Authorization: `Bearer ${tokenLocal}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          console.error("Erro ao buscar imóveis:", data);
          setImoveisBuscaLista([]);
          return;
        }

        const lista = Array.isArray(data) ? data : Array.isArray(data?.imoveis) ? data.imoveis : Array.isArray(data?.recordset) ? data.recordset : [];

        setImoveisBuscaLista(lista);
      })
      .catch((err) => {
        console.error("Erro ao buscar imóveis:", err);
        setImoveisBuscaLista([]);
      });
  }, []);

  // =========================
  // OPCIONAIS EM VERMELHO
  // =========================
  const camposOpcionais = [
    "complemento",
    "numeroRelogio",
    "subNumeroRelogio",
    "categoriaConsumo",
    "nomeProprietario",
    "cpfProprietario",
    "nomeTitular",
    "cpfTitular",
    "status",
    "debitoAberto",
    "valorDebito",
    "competencia",
    "atualizado",
    "observacoes",
    "matricula",
  ];

  const estaVazio = (v) => !String(v ?? "").trim();

  const classeCampoOpcional = (campo) => {
    if (!mostrarPendencias) return "";
    return camposOpcionais.includes(campo) && estaVazio(formData[campo]) ? "campo-pendente" : "";
  };

  // =========================
  // CAMPOS DE TEXTO EM MAIÚSCULO
  // =========================
  const CAMPOS_MAIUSCULOS_RELOGIO = [
    "apelido",
    "complemento",
    "categoriaConsumo",
    "nomeProprietario",
    "nomeTitular",
    "status",
    "debitoAberto",
    "atualizado",
    "observacoes",
    "matricula",
  ];

  // =========================
  // HANDLE CHANGE COM MÁSCARAS
  // =========================
  const handleChange = (e) => {
    if (formTravado) return;

    const { name, value } = e.target;
    let valorFinal = value;

    if (name === "cpfProprietario" || name === "cpfTitular") {
      valorFinal = maskCpfCnpj(value);
    } else if (name === "numeroRelogio" || name === "subNumeroRelogio") {
      valorFinal = maskNumero(value);
    } else if (name === "valorDebito") {
      valorFinal = maskMoney(value);
    } else if (CAMPOS_MAIUSCULOS_RELOGIO.includes(name)) {
      valorFinal = String(value ?? "").toUpperCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: valorFinal,
    }));
  };

  // =========================
  // VISUALIZAR
  // =========================
  const handleVisualizar = (item) => {
    if (!podeVisualizar) {
      alert("Você não tem permissão para visualizar.");
      return;
    }

    const codigo = item.codigo;

    setFormData({
      codigo: item.codigo || "",
      apelido: item.apelido || "",
      complemento: item.complemento || "",
      numeroRelogio: item.numeroRelogio || "",
      subNumeroRelogio: item.subNumeroRelogio || "",
      categoriaConsumo: item.categoriaConsumo || "",
      nomeProprietario: item.nomeProprietario || "",
      cpfProprietario: item.cpfProprietario || "",
      nomeTitular: item.nomeTitular || "",
      cpfTitular: item.cpfTitular || "",
      status: item.status || "",
      debitoAberto: item.debitoAberto || "",
      valorDebito: item.valorDebito || "",
      competencia: item.competencia || "",
      atualizado: item.atualizado || "",
      observacoes: item.observacoes || "",
      matricula: item.matricula || "",
      matriculaId: item.matriculaId || "",
    });

    setIdVisualizando(codigo);
    setEditandoCodigo(null);
    setModoVisualizacao(true);

    setMostrarPendencias(true);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================
  // SALVAR (CADASTRAR/ATUALIZAR)
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMostrarPendencias(true);

    const tokenLocal = localStorage.getItem("token");
    if (!tokenLocal) {
      alert("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!editandoCodigo && !podeCadastrar) {
      alert("Você não tem permissão para cadastrar.");
      return;
    }
    if (editandoCodigo && !podeEditar) {
      alert("Você não tem permissão para editar.");
      return;
    }

    try {
      const url = editandoCodigo ? `/api/relogio-energia/${editandoCodigo}` : "/api/relogio-energia";
      const method = editandoCodigo ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenLocal}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const codigoSalvo = editandoCodigo || data?.codigo || formData.codigo;

        alert(editandoCodigo ? "Registro atualizado com sucesso!" : "Cadastro realizado com sucesso!");

        if (codigoSalvo && gedPendentes.length > 0) {
          const okGed = await enviarGED(codigoSalvo, gedPendentes);

          if (okGed) {
            setGedPendentes([]);
            await carregarRegistros();
            await abrirGED(codigoSalvo);
            alert("Anexos GED pendentes enviados!");
          } else {
            await carregarRegistros();
            alert("Registro salvo, mas falhou ao enviar os anexos GED pendentes.");
          }
        } else {
          await carregarRegistros();
        }

        if (!editandoCodigo) {
          setPaginaAtual(1);
        }

        resetForm();
      } else {
        alert(data?.mensagem || data?.erro || "Erro ao salvar.");
      }
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("Erro ao salvar.");
    }
  };

  // =========================
  // EXCLUIR
  // =========================
  const handleDelete = async (codigo) => {
    if (!codigo) {
      alert("Erro: código inválido para exclusão.");
      return;
    }

    if (!podeExcluir) {
      alert("Você não tem permissão para excluir.");
      return;
    }

    const confirmar = window.confirm("Tem certeza que deseja excluir este registro?");
    if (!confirmar) return;

    const tokenLocal = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/relogio-energia/${codigo}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tokenLocal}` },
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        alert(data.mensagem || "Registro excluído com sucesso!");
        setRegistros((prev) => prev.filter((r) => r.codigo !== codigo));
        setPaginaAtual(1);
        resetForm();
      } else {
        alert(data.mensagem || data.erro || "Erro ao excluir registro.");
      }
    } catch (err) {
      console.error("Erro ao excluir:", err);
      alert("Erro ao excluir registro.");
    }
  };

  const temGedNoForm =
    ((editandoCodigo || idVisualizando) && arquivos.length > 0) ||
    gedPendentes.length > 0 ||
    (registros.find((r) => r.codigo === (editandoCodigo || idVisualizando))?.qtdArquivos || 0) > 0;

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="relogioagua-container">
        <h2>Relógios de Energia</h2>

        {/* BLOCO DE BUSCA (sempre visível) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Filtrar Relógio de Energia"
              value={filtroCodigo}
              onChange={(e) => {
                setFiltroCodigo(e.target.value);
                setPaginaAtual(1);
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltroCodigo("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              startIcon={<i className="fas fa-copy" />}
              onClick={async () => {
                const texto = registrosFiltrados.map((registro) => registro.codigo).join("\n");

                if (!texto.trim()) {
                  alert("Nada para copiar.");
                  return;
                }

                const ok = await copiarTexto(texto);

                alert(ok ? "Códigos copiados!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={() => {
                const BOM_BYTES = new Uint8Array([0xef, 0xbb, 0xbf]);

                const csvEscape = (value) => {
                  const texto = String(value ?? "");

                  if (/[;"\n\r]/.test(texto)) {
                    return `"${texto.replace(/"/g, '""')}"`;
                  }

                  return texto;
                };

                const excelText = (value) => `="${String(value ?? "")}"`;

                const header = [
                  "Código",
                  "Apelido",
                  "Complemento",
                  "N° Relógio",
                  "Sub N° Relógio",
                  "Categoria",
                  "Nome Proprietário",
                  "CPF Proprietário",
                  "Nome Titular",
                  "CPF Titular",
                  "Status",
                  "Débito Aberto",
                  "Valor Débito",
                  "Competência",
                  "Atualizado",
                  "Observações",
                  "Matrícula",
                ];

                const linhas = registrosFiltrados.map((registro) => {
                  const cpfProprietario = maskCpfCnpj(String(registro.cpfProprietario ?? ""));

                  const cpfTitular = maskCpfCnpj(String(registro.cpfTitular ?? ""));

                  return [
                    excelText(registro.codigo),
                    csvEscape(registro.apelido),
                    csvEscape(registro.complemento),
                    excelText(registro.numeroRelogio),
                    excelText(registro.subNumeroRelogio),
                    csvEscape(registro.categoriaConsumo),
                    csvEscape(registro.nomeProprietario),
                    excelText(cpfProprietario),
                    csvEscape(registro.nomeTitular),
                    excelText(cpfTitular),
                    csvEscape(registro.status),
                    csvEscape(registro.debitoAberto),
                    excelText(registro.valorDebito),
                    excelText(registro.competencia),
                    csvEscape(registro.atualizado),
                    csvEscape(registro.observacoes),
                    excelText(registro.matricula),
                  ];
                });

                const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\r\n");

                const blob = new Blob([BOM_BYTES, csv], {
                  type: "text/csv;charset=utf-8;",
                });

                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");

                link.href = url;
                link.setAttribute("download", "relogios_energia.csv");

                document.body.appendChild(link);
                link.click();
                link.remove();

                URL.revokeObjectURL(url);
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* ✅ BOTÃO MINIMIZAR/EXPANDIR (sempre visível) */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
              disabled={!podeVisualizar}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
              disabled={!podeVisualizar}
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* LISTAGEM (minimiza só tabela + paginação) */}
        {!gridMinimizado && (
          <div className="bloco-listagem">
            {registrosFiltrados.length === 0 ? (
              <p>Nenhum registro encontrado.</p>
            ) : (
              <>
                <table className="tabela-registro tabela-relogio-energia">
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "24%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Apelido</th>
                      <th>N° Relógio</th>
                      <th>Status</th>
                      <th>Matrícula</th>
                      <th className="col-acoes">Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((item) => (
                      <tr key={item.codigo}>
                        <td>{item.codigo}</td>
                        <td>{item.apelido}</td>
                        <td>{item.numeroRelogio}</td>
                        <td>{item.status}</td>
                        <td>{item.matricula}</td>

                        <td className="col-acoes">
                          <button
                            className={`btn-ged-lista ${Number(item.qtdArquivos || 0) > 0 ? "btn-ged-ok" : ""}`}
                            type="button"
                            onClick={() => item.codigo && abrirGED(item.codigo)}
                          >
                            GED
                            {Number(item.qtdArquivos || 0) > 0 && <span className="ged-badge">{item.qtdArquivos}</span>}
                          </button>

                          {podeVisualizar && (
                            <button className="btn-editar" type="button" onClick={() => handleVisualizar(item)}>
                              <FaEye />
                              Visualizar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* PAGINAÇÃO */}
                <TablePagination
                  component="div"
                  count={registrosFiltrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => {
                    setPaginaAtual(novaPagina + 1);
                  }}
                  rowsPerPage={registrosPorPagina}
                  rowsPerPageOptions={[5, 10, 25]}
                  onRowsPerPageChange={(e) => {
                    setRegistrosPorPagina(parseInt(e.target.value, 10));
                    setPaginaAtual(1);
                  }}
                  labelRowsPerPage="Relógios por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  sx={{
                    width: "100%",
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",
                    mt: 1,

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 58,
                      paddingLeft: 0,
                      paddingRight: 0,
                    },

                    "& .MuiTablePagination-spacer": {
                      flex: "1 1 auto",
                    },

                    "& .MuiTablePagination-selectLabel": {
                      color: "var(--ig-t-ffffff)",
                      fontSize: "0.85rem",
                      margin: 0,
                    },

                    "& .MuiTablePagination-displayedRows": {
                      color: "var(--ig-t-ffffff)",
                      fontSize: "0.85rem",
                      margin: 0,
                    },

                    "& .MuiTablePagination-select": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },

                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                    },
                  }}
                />
                {!podeCadastrar && (
                  <div
                    style={{
                      marginTop: "20px",
                      textAlign: "center",
                      color: "gold",
                      fontSize: "1.1rem",
                      display: "flex",
                      justifyContent: "center",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <i className="fas fa-lock"></i>
                    Você possui acesso apenas de visualização.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* MODAL SELECIONAR IMÓVEL PELO APELIDO */}
        <Dialog
          open={modalSelecionarImovel}
          onClose={() => {
            setModalSelecionarImovel(false);
            setFiltroImovel("");
            setPaginaImovel(0);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
              maxHeight: "88vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
              px: 3,
              py: 2,
            }}
          >
            Selecionar Imóvel
            <IconButton
              type="button"
              onClick={() => {
                setModalSelecionarImovel(false);
                setFiltroImovel("");
                setPaginaImovel(0);
              }}
            >
              <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <TextField
                value={filtroImovel}
                onChange={(e) => {
                  setFiltroImovel(e.target.value);
                  setPaginaImovel(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setModalSelecionarImovel(false);
                    setFiltroImovel("");
                    setPaginaImovel(0);
                  }
                }}
                placeholder="Código, apelido, endereço, bairro ou cidade..."
                fullWidth
                size="small"
                autoFocus
                sx={muiCampoSx}
              />

              <Button
                type="button"
                variant="contained"
                sx={{
                  minWidth: 120,
                  height: 56,
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: "#2196f3",

                  "&:hover": {
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                Pesquisar
              </Button>
            </Box>

            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                width: "100%",

                "& .MuiTableCell-root": {
                  color: "var(--ig-t-ffffff)",
                  borderBottom: "1px solid var(--ig-b-444444)",
                  py: 1.2,
                },

                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "var(--ig-f-1e1e1e)",
                  fontWeight: 700,
                },

                "& .MuiTableBody-root .MuiTableRow-root": {
                  cursor: "pointer",
                },

                "& .MuiTableBody-root .MuiTableRow-root:hover": {
                  backgroundColor: "var(--ig-f-255-255-255-a008)",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "9%" }}>Código</TableCell>

                  <TableCell sx={{ width: "12%" }}>Apelido</TableCell>

                  <TableCell sx={{ width: "26%" }}>Endereço</TableCell>

                  <TableCell sx={{ width: "26%" }}>Bairro</TableCell>

                  <TableCell sx={{ width: "16%" }}>Cidade/UF</TableCell>

                  <TableCell sx={{ width: "11%" }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {imoveisPaginadosBusca.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        py: 3,
                        color: "var(--ig-t-cccccc) !important",
                      }}
                    >
                      Nenhum imóvel encontrado.
                    </TableCell>
                  </TableRow>
                ) : (
                  imoveisPaginadosBusca.map((imovel) => (
                    <TableRow
                      key={imovel.id || imovel.codigo || imovel.codigoMatricula || imovel.apelido}
                      hover
                      onClick={() => selecionarImovelApelido(imovel)}
                    >
                      <TableCell>{imovel.codigo || imovel.codigoMatricula || ""}</TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {imovel.apelido || ""}
                      </TableCell>

                      <TableCell>{imovel.endereco || ""}</TableCell>

                      <TableCell>{imovel.bairro || ""}</TableCell>

                      <TableCell>{[imovel.cidade, imovel.estado || imovel.uf].filter(Boolean).join("/")}</TableCell>

                      <TableCell>{imovel.status || ""}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={imoveisFiltradosBusca.length}
              page={paginaImovel}
              onPageChange={(_, novaPagina) => {
                setPaginaImovel(novaPagina);
              }}
              rowsPerPage={linhasPorPaginaImovel}
              onRowsPerPageChange={(e) => {
                setLinhasPorPaginaImovel(parseInt(e.target.value, 10));
                setPaginaImovel(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--ig-f-2c2c2c)",
                      color: "var(--ig-t-ffffff)",

                      "& .MuiMenuItem-root": {
                        color: "var(--ig-t-ffffff)",
                      },

                      "& .MuiMenuItem-root:hover": {
                        backgroundColor: "var(--ig-f-3a3a3a)",
                      },

                      "& .MuiMenuItem-root.Mui-selected": {
                        backgroundColor: "#424242 !important",
                      },
                    },
                  },
                },
              }}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",

                "& .MuiTablePagination-selectLabel": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-displayedRows": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-select": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-selectIcon": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-actions button": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </DialogContent>
        </Dialog>

        {/* MODAL GED */}
        {modalGED && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - Relógio de Energia {codigoSelecionadoGED}</h3>

              {arquivos.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este registro.</p>
              ) : (
                <ul className="ged-lista-arquivos">
                  {arquivos.map((arq) => {
                    const arquivoId = arq.id || arq.nome;

                    return (
                      <li key={arquivoId} className="ged-item-arquivo">
                        <span className="ged-nome-arquivo">{arq.nome}</span>

                        <div className="ged-acoes-arquivo">
                          <button type="button" className="btn-visualizar-ged" onClick={() => visualizarArquivoGED(codigoSelecionadoGED, arquivoId, arq.nome)}>
                            <FaEye />
                            Visualizar
                          </button>

                          <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(codigoSelecionadoGED, arquivoId, arq.nome)}>
                            Baixar
                          </button>

                          <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivoGED(codigoSelecionadoGED, arquivoId)}>
                            <i className="fas fa-trash" />
                            Excluir
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={() => setModalGED(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        {/* FORMULÁRIO */}
        {(podeCadastrar || podeVisualizar) && (
          <div className="bloco-formulario relogio-energia-formulario">
            <form className="relogioagua-form form-grid" onSubmit={handleSubmit}>
              {/* ABAS DO FORMULÁRIO */}
              <div className="abas-formulario relogio-abas-formulario">
                <button type="button" className={`aba ${!mostrarSituacao ? "ativa" : ""}`} onClick={() => setMostrarSituacao(false)}>
                  Dados do Relógio
                </button>

                <button type="button" className={`aba ${mostrarSituacao ? "ativa" : ""}`} onClick={() => setMostrarSituacao(true)}>
                  Situação do Relógio
                </button>
              </div>

              {/* ABA — DADOS DO RELÓGIO */}
              {!mostrarSituacao && (
                <div className="conteudo-aba-formulario">
                  {/* LINHA 1 — APELIDO E COMPLEMENTO */}
                  <div className="form-row relogio-row-1">
                    <TextField
                      label="Apelido*"
                      name="apelido"
                      value={formData.apelido || ""}
                      onClick={() => {
                        if (formTravado) return;

                        setFiltroImovel("");
                        setPaginaImovel(0);
                        setModalSelecionarImovel(true);
                      }}
                      disabled={formTravado}
                      fullWidth
                      size="small"
                      sx={{
                        ...muiCampoSx,

                        "& .MuiOutlinedInput-root": {
                          ...muiCampoSx["& .MuiOutlinedInput-root"],
                          cursor: formTravado ? "default" : "pointer",
                        },

                        "& .MuiInputBase-input": {
                          cursor: formTravado ? "default" : "pointer",
                          color: "var(--ig-t-ffffff)",
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <SearchIcon
                            sx={{
                              color: "var(--ig-t-ffffff)",
                              cursor: formTravado ? "default" : "pointer",
                            }}
                          />
                        ),
                      }}
                      placeholder="Clique para selecionar o imóvel"
                    />

                    <TextField
                      select
                      label="Complemento"
                      name="complemento"
                      value={formData.complemento}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("complemento")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="">Selecione o Complemento</MenuItem>

                      {locais.map((local) => (
                        <MenuItem key={local.id || local.nome} value={local.nome}>
                          {local.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>

                  {/* LINHA 2 — DADOS DO MEDIDOR */}
                  <div className="form-row relogio-row-2">
                    <TextField
                      label="Nº Relógio"
                      name="numeroRelogio"
                      value={formData.numeroRelogio}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("numeroRelogio")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Sub Nº Relógio"
                      name="subNumeroRelogio"
                      value={formData.subNumeroRelogio}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("subNumeroRelogio")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Categoria Consumo"
                      name="categoriaConsumo"
                      value={formData.categoriaConsumo}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("categoriaConsumo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </div>

                  {/* LINHA 3 — PROPRIETÁRIO */}
                  <div className="form-row relogio-row-3">
                    <TextField
                      label="Nome Proprietário"
                      name="nomeProprietario"
                      value={formData.nomeProprietario}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("nomeProprietario")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="CPF/CNPJ Proprietário"
                      name="cpfProprietario"
                      value={formData.cpfProprietario}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("cpfProprietario")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Proprietário igual ao Proprietário da Matrícula?"
                      value={proprietarioIgual}
                      disabled
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </div>

                  {/* LINHA 4 — TITULAR */}
                  <div className="form-row relogio-row-3">
                    <TextField
                      label="Nome Titular / Consumidor"
                      name="nomeTitular"
                      value={formData.nomeTitular}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("nomeTitular")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="CPF/CNPJ Titular / Consumidor"
                      name="cpfTitular"
                      value={formData.cpfTitular}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("cpfTitular")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField label="Titular é igual ao Usuário / Locatário?" value={titularIgual} disabled fullWidth size="small" sx={muiCampoSx} />
                  </div>

                  {/* LINHA 5 — MATRÍCULA */}
                  <div className="form-row relogio-row-full">
                    <div className="relogio-busca-matricula">
                      <span className="relogio-label-matricula">Matrícula*</span>

                      {formTravado ? (
                        <TextField value={formData.matricula} disabled error={erroCampoMUI("matricula")} fullWidth size="small" sx={muiCampoSx} />
                      ) : (
                        <BuscarMatricula
                          onSelecionar={(mat) => {
                            setFormData((prev) => ({
                              ...prev,
                              matriculaId: mat.id,
                              matricula: String(`${mat.codigoMatricula || ""} - ${mat.apelido || ""}`)
                                .trim()
                                .toUpperCase(),
                            }));

                            setNomeProprietarioMatricula(mat.nomeProprietario ?? "");

                            setNomeLocatario(mat.nomeLocatario ?? "");
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* LINHA 6 — OBSERVAÇÃO */}
                  <div className="form-row relogio-row-full">
                    <TextField
                      label="Observação"
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("observacoes")}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      sx={muiCampoSx}
                    />
                  </div>
                </div>
              )}

              {/* ABA — SITUAÇÃO DO RELÓGIO */}
              {mostrarSituacao && (
                <div className="conteudo-aba-formulario">
                  <div className="form-row relogio-row-situacao">
                    <TextField
                      select
                      label="Status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("status")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="">Selecione Status</MenuItem>

                      <MenuItem value="ATIVO">Ativo</MenuItem>

                      <MenuItem value="INATIVO">Inativo</MenuItem>

                      <MenuItem value="CORTE">Corte</MenuItem>

                      <MenuItem value="SUSPENSO">Suspenso</MenuItem>
                    </TextField>

                    <TextField
                      select
                      label="Débito em Aberto?"
                      name="debitoAberto"
                      value={formData.debitoAberto}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("debitoAberto")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="NÃO">Não</MenuItem>

                      <MenuItem value="SIM">Sim</MenuItem>
                    </TextField>

                    {formData.debitoAberto === "SIM" && (
                      <TextField
                        label="Valor Débitos em Aberto"
                        name="valorDebito"
                        value={formData.valorDebito}
                        onChange={handleChange}
                        disabled={formTravado}
                        error={erroCampoMUI("valorDebito")}
                        fullWidth
                        size="small"
                        sx={muiCampoSx}
                      />
                    )}
                  </div>

                  <div className="form-row relogio-row-situacao">
                    <TextField
                      label="Mês da Última Competência"
                      type="month"
                      name="competencia"
                      value={formData.competencia}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("competencia")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />

                    <TextField
                      select
                      label="Cadastro Atualizado?"
                      name="atualizado"
                      value={formData.atualizado}
                      onChange={handleChange}
                      disabled={formTravado}
                      error={erroCampoMUI("atualizado")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      slotProps={{
                        select: menuPropsDark,
                      }}
                    >
                      <MenuItem value="SIM">Sim</MenuItem>

                      <MenuItem value="NÃO">Não</MenuItem>
                    </TextField>
                  </div>
                </div>
              )}
              {/* AÇÕES DO FORM */}
              <div className="form-botoes relogio-form-botoes">
                {modoVisualizacao && !editandoCodigo && podeEditar && (
                  <button
                    type="button"
                    className="btn-cadastrar"
                    onClick={() => {
                      setEditandoCodigo(idVisualizando);
                      setModoVisualizacao(false);
                    }}
                  >
                    <FaEdit />
                    Editar
                  </button>
                )}

                {(!modoVisualizacao || editandoCodigo) && (
                  <button type="submit" className="btn-cadastrar">
                    <FaSave />
                    {editandoCodigo ? "Atualizar" : "Cadastrar"}
                  </button>
                )}

                {(modoVisualizacao || editandoCodigo) && (
                  <button type="button" className="btn-acao" onClick={resetForm}>
                    <FaTimes />
                    Cancelar
                  </button>
                )}

                {podeMostrarExcluirNoForm && podeExcluir && (
                  <button type="button" className="btn-excluir" onClick={() => handleDelete(idVisualizando)}>
                    <FaTrash />
                    Excluir
                  </button>
                )}

                <button
                  type="button"
                  className="btn-ged btn-ged-form"
                  onClick={() => {
                    const input = document.getElementById("uploadGEDRelogioEnergia");

                    input?.click();
                  }}
                  disabled={!podeCadastrar && !idVisualizando}
                >
                  <FaPaperclip />
                  GED
                  {(editandoCodigo || idVisualizando) &&
                    (() => {
                      const codigoAtual = editandoCodigo || idVisualizando;

                      const registroAtual = registros.find((registro) => registro.codigo === codigoAtual);

                      const quantidade = arquivos.length || Number(registroAtual?.qtdArquivos || 0);

                      return quantidade > 0 ? <span className="ged-badge">{quantidade}</span> : null;
                    })()}
                  {gedPendentes.length > 0 && !editandoCodigo && !idVisualizando && <span className="ged-badge">{gedPendentes.length}</span>}
                </button>
              </div>

              {/* GED DO FORMULÁRIO */}
              {(gedPendentes.length > 0 || ((editandoCodigo || idVisualizando) && arquivos.length > 0)) && (
                <div className="ged-formulario-container">
                  <div className="ged-formulario-cabecalho">
                    <div>
                      <h3>Arquivos GED</h3>

                      <span>
                        {gedPendentes.length > 0 && `${gedPendentes.length} arquivo(s) pendente(s)`}

                        {gedPendentes.length > 0 && arquivos.length > 0 && " • "}

                        {arquivos.length > 0 && `${arquivos.length} arquivo(s) anexado(s)`}
                      </span>
                    </div>

                    <FaPaperclip className="ged-formulario-icone" />
                  </div>

                  {/* ARQUIVOS PENDENTES */}
                  {!formTravado && gedPendentes.length > 0 && (
                    <div className="ged-formulario-secao">
                      <div className="ged-formulario-subtitulo">Arquivos pendentes para envio</div>

                      <div className="ged-formulario-lista">
                        {gedPendentes.map((arquivo, indice) => (
                          <div key={`${arquivo.name}-${indice}`} className="ged-formulario-item ged-formulario-item-pendente">
                            <div className="ged-formulario-arquivo">
                              <FaPaperclip />

                              <div className="ged-formulario-dados">
                                <span className="ged-formulario-nome" title={arquivo.name}>
                                  {arquivo.name}
                                </span>

                                <small>Aguardando o cadastro para envio</small>
                              </div>
                            </div>

                            <div className="ged-formulario-acoes">
                              <button
                                type="button"
                                className="ged-formulario-btn remover"
                                onClick={() => setGedPendentes((anteriores) => anteriores.filter((_, itemIndice) => itemIndice !== indice))}
                              >
                                <FaTimes />
                                Remover
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ARQUIVOS JÁ ANEXADOS */}
                  {(editandoCodigo || idVisualizando) && arquivos.length > 0 && (
                    <div className="ged-formulario-secao">
                      <div className="ged-formulario-subtitulo">Arquivos anexados neste registro</div>

                      <div className="ged-formulario-lista">
                        {arquivos.map((arquivo) => {
                          const arquivoId = arquivo.id || arquivo.nome;

                          return (
                            <div key={arquivoId} className="ged-formulario-item">
                              <div className="ged-formulario-arquivo">
                                <FaPaperclip />

                                <div className="ged-formulario-dados">
                                  <span className="ged-formulario-nome" title={arquivo.nome}>
                                    {arquivo.nome}
                                  </span>

                                  <small>Arquivo anexado ao registro</small>
                                </div>
                              </div>

                              <div className="ged-formulario-acoes">
                                <button
                                  type="button"
                                  className="ged-formulario-btn visualizar"
                                  onClick={() => visualizarArquivoGED(editandoCodigo || idVisualizando, arquivoId, arquivo.nome)}
                                >
                                  <FaEye />
                                  Visualizar
                                </button>

                                <button
                                  type="button"
                                  className="ged-formulario-btn baixar"
                                  onClick={() => baixarArquivoGED(editandoCodigo || idVisualizando, arquivoId, arquivo.nome)}
                                >
                                  <i className="fas fa-download" />
                                  Baixar
                                </button>

                                {podeExcluir && (
                                  <button
                                    type="button"
                                    className="ged-formulario-btn excluir"
                                    onClick={() => excluirArquivoGED(editandoCodigo || idVisualizando, arquivoId)}
                                  >
                                    <FaTrash />
                                    Excluir
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Input escondido para upload GED */}
              <input
                type="file"
                id="uploadGEDRelogioEnergia"
                multiple
                style={{ display: "none" }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;

                  e.target.value = "";

                  const codigoAtual = editandoCodigo || idVisualizando;

                  if (!codigoAtual) {
                    setGedPendentes((prev) => [...prev, ...files]);
                    alert(`Arquivos preparados (${files.length}). Clique em Cadastrar para anexar.`);
                    return;
                  }

                  const ok = await enviarGED(codigoAtual, files);

                  if (ok) {
                    alert("Arquivos enviados!");
                    await abrirGED(codigoAtual);
                    await carregarRegistros();
                  } else {
                    alert("Falha ao enviar arquivos.");
                  }
                }}
              />
            </form>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default RelogioEnergia;
